import React, { useState, useContext } from 'react';
import {
  Button,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useLocation } from 'react-router';
import { AppContext } from '../../App';
import { IDocumentUnscoped } from '../../types/my-types';
import {
  DocumentDataExclusionReasons,
  DocumentDataStatusFilters,
  USER_SELECTABLE_DOCUMENT_DATA_STATUSES,
} from '../../types/packager-types';
import {
  SYSTEM_OWNED_EXCLUSION_REASONS,
  isSystemOwnedExclusionReason,
  renderExclusionReasonMenuItems,
} from '../common/exclusionReasonMenuItems';
import { extractApiError } from '../../utils/extractApiError';
import { useDocumentDataBulkStatusUpdate } from '../hooks/documentData/useDocumentDataBulkStatusUpdate';
import DialogGlynt, { FormControlStyled, FormLabelStyled } from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import useGlynt from '../hooks/useGlynt';

const NO_CHANGE = 'NO_CHANGE';

interface DocumentTableBulkStatusUpdateProps {
  documents: IDocumentUnscoped[];
  selectedDocumentIds?: string[];
  resolveSelectedDocumentIds?: () => Promise<string[]>;
  resolvingLabel?: string;
  buttonDisabled?: boolean;
  onSuccess?: () => void;
}

function DocumentTableBulkStatusUpdate({
  selectedDocumentIds,
  resolveSelectedDocumentIds,
  resolvingLabel,
  buttonDisabled,
  onSuccess,
}: DocumentTableBulkStatusUpdateProps) {
  const { setToast } = useContext(AppContext);
  const glynt = useGlynt();

  const [open, setOpen] = useState(false);
  const [documentDataStatus, setDocumentDataStatus] = useState<string>(NO_CHANGE);
  const [exclusionReason, setExclusionReason] = useState<DocumentDataExclusionReasons>(
    DocumentDataExclusionReasons.OUT_OF_SCOPE
  );
  const [escalationDetail, setEscalationDetail] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resolvedIds, setResolvedIds] = useState<string[] | null>(null);
  const [isResolving, setIsResolving] = useState(false);

  const isExcludingDocumentData = documentDataStatus === DocumentDataStatusFilters.EXCLUDED;
  const isEscalatingDocumentData = documentDataStatus === DocumentDataStatusFilters.ESCALATED;
  const escalationMissing = isEscalatingDocumentData && !escalationDetail.trim();

  const queryClient = useQueryClient();

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const urlSelectedIds = searchParams.get('selectedIds')?.split(',') || [];
  const selectedIds = resolveSelectedDocumentIds
    ? (resolvedIds ?? [])
    : (selectedDocumentIds ?? urlSelectedIds);

  const { mutateAsync: bulkUpdateDocumentDataStatus, isPending: isUpdatingDocumentData } =
    useDocumentDataBulkStatusUpdate({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['documents'],
        });
      },
      onError: (error: AxiosError) => {
        setToast({
          variant: 'error',
          open: true,
          message: extractApiError(error, 'Failed to update document data statuses.'),
        });
      },
    });

  const isLoading = isUpdatingDocumentData || isSubmitting;

  const closeModal = () => {
    setOpen(false);
    setDocumentDataStatus(NO_CHANGE);
    setExclusionReason(DocumentDataExclusionReasons.OUT_OF_SCOPE);
    setEscalationDetail('');
    setResolvedIds(null);
    setIsResolving(false);
  };

  const handleOpen = async () => {
    setOpen(true);
    if (resolveSelectedDocumentIds) {
      setIsResolving(true);
      setResolvedIds(null);
      try {
        const ids = await resolveSelectedDocumentIds();
        setResolvedIds(ids);
      } catch (error) {
        setToast({
          variant: 'error',
          open: true,
          message: extractApiError(
            error as AxiosError,
            'Failed to load documents for status update.'
          ),
        });
        closeModal();
        return;
      } finally {
        setIsResolving(false);
      }
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (documentDataStatus === NO_CHANGE) {
        setToast({
          variant: 'warning',
          open: true,
          message: 'Please select a status to update.',
        });
        setIsSubmitting(false);
        return;
      }

      // Fetch document data for each selected document. The response carries the current
      // exclusion_reason, which is needed below -- withholding CONCATENATED from the
      // dropdown stops it being SET, but says nothing about rows that already hold it.
      const glyntClient = await glynt;
      const documentDataIds: string[] = [];
      const protectedIds: string[] = [];
      for (const docId of selectedIds) {
        try {
          const response = await glyntClient.documentData.list({ document: docId, limit: 1 });
          const row = response.results?.[0];
          if (!row) continue;
          if (isExcludingDocumentData && isSystemOwnedExclusionReason(row.exclusion_reason)) {
            // A split parent's CONCATENATED is load-bearing: the packager keys
            // lineage/source-file delivery on it, and dedup/heal treats such a row as a
            // dead container it must not resurrect. Overwriting it in bulk is silent and
            // unrecoverable, so skip the row rather than clobber it.
            protectedIds.push(row.id);
            continue;
          }
          documentDataIds.push(row.id);
        } catch (err) {
          console.warn(`Failed to fetch document data for document ${docId}:`, err);
        }
      }

      if (protectedIds.length > 0 && documentDataIds.length === 0) {
        setToast({
          variant: 'warning',
          open: true,
          message:
            `Skipped ${protectedIds.length} document(s) whose exclusion reason is set by the ` +
            `system (e.g. a split parent). Nothing else was selected, so no changes were made.`,
        });
        setIsSubmitting(false);
        return;
      }

      if (documentDataIds.length === 0) {
        setToast({
          variant: 'warning',
          open: true,
          message:
            'No document data found for selected documents. They may not have been processed yet.',
        });
        setIsSubmitting(false);
        return;
      }

      const items = documentDataIds.map((id) => {
        const item: {
          id: string;
          status: string;
          exclusion_reason?: string;
          escalation_detail?: string;
        } = {
          id,
          status: documentDataStatus,
        };
        if (isExcludingDocumentData) item.exclusion_reason = exclusionReason;
        if (isEscalatingDocumentData) item.escalation_detail = escalationDetail.trim();
        return item;
      });

      await bulkUpdateDocumentDataStatus(items);
      setToast({
        variant: protectedIds.length > 0 ? 'warning' : 'success',
        open: true,
        message:
          protectedIds.length > 0
            ? `Updated ${items.length} document(s). Skipped ${protectedIds.length} whose ` +
              `exclusion reason is set by the system (e.g. a split parent).`
            : 'Statuses updated successfully.',
      });
      onSuccess?.();
      closeModal();
    } catch (error) {
      console.error('Error updating statuses:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    documentDataStatus !== NO_CHANGE &&
    !isLoading &&
    !isResolving &&
    !escalationMissing &&
    selectedIds.length > 0;

  return (
    <>
      <Button
        data-cy="btn-manifest-bulk-status-update"
        disabled={buttonDisabled ?? (resolveSelectedDocumentIds ? false : selectedIds.length === 0)}
        onClick={handleOpen}
        sx={{
          height: 32,
          px: 1.5,
          borderRadius: '6px',
          border: '1px solid #1D9AAA',
          bgcolor: '#fff',
          fontSize: '12.5px',
          fontWeight: 500,
          color: '#1D9AAA',
          textTransform: 'none',
          whiteSpace: 'nowrap',
          '&:hover': { borderColor: '#178a99', bgcolor: 'rgba(29,154,170,0.04)' },
          '&.Mui-disabled': { borderColor: '#BDC3D2', color: '#B0B0B0', bgcolor: '#fff' },
        }}
      >
        Update Status
      </Button>

      <DialogGlynt
        open={open}
        closeModal={closeModal}
        width="450px"
        cypressId="dialog-bulk-status-update"
      >
        <DialogTitleGlynt title="Bulk Update Status" closeModal={closeModal} />
        <DialogContentGlynt>
          {isResolving ? (
            <Box
              data-cy="bulk-status-resolving"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                py: 4,
              }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                {resolvingLabel || 'Loading documents…'}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Update status for {selectedIds.length} selected document
                  {selectedIds.length !== 1 ? 's' : ''}.
                </Typography>
              </Box>

              <FormControlStyled fullWidth>
                <FormLabelStyled>DOCUMENT DATA STATUS</FormLabelStyled>
                <Select
                  id="document-data-status-select"
                  data-cy="select-bulk-document-data-status"
                  value={documentDataStatus}
                  onChange={(e) => setDocumentDataStatus(e.target.value)}
                  variant="outlined"
                  size="small"
                >
                  <MenuItem value={NO_CHANGE}>
                    <em>No Change</em>
                  </MenuItem>
                  {USER_SELECTABLE_DOCUMENT_DATA_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControlStyled>

              {isExcludingDocumentData && (
                <FormControlStyled fullWidth>
                  <FormLabelStyled>EXCLUSION REASON *</FormLabelStyled>
                  <Select
                    value={exclusionReason}
                    onChange={(e) =>
                      setExclusionReason(e.target.value as DocumentDataExclusionReasons)
                    }
                    variant="outlined"
                    size="small"
                    data-cy="select-bulk-exclusion-reason"
                  >
                    {renderExclusionReasonMenuItems({ omit: SYSTEM_OWNED_EXCLUSION_REASONS })}
                  </Select>
                  <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    Applied to the document data.
                  </Typography>
                </FormControlStyled>
              )}

              {isEscalatingDocumentData && (
                <FormControlStyled fullWidth>
                  <FormLabelStyled>ESCALATION DETAIL *</FormLabelStyled>
                  <TextField
                    value={escalationDetail}
                    onChange={(e) => setEscalationDetail(e.target.value)}
                    placeholder="Why is this being escalated?"
                    fullWidth
                    multiline
                    minRows={2}
                    variant="outlined"
                    size="small"
                    data-cy="input-bulk-dd-escalation-detail"
                  />
                  <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    Same note is applied to every selected document data row.
                  </Typography>
                </FormControlStyled>
              )}
            </>
          )}
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleSubmit}
          closeModal={closeModal}
          cancelCypressId="btn-bulk-status-cancel"
          submitCypressId="btn-bulk-status-submit"
          clearCypressId="btn-bulk-status-clear"
          submitButtonText="Update"
          submitLoading={isLoading}
          showCancelButton={true}
          showClearButton={false}
          submitDisabled={!canSubmit}
        />
      </DialogGlynt>
    </>
  );
}

export default DocumentTableBulkStatusUpdate;
